import { useState, useRef, useEffect } from "react";
// import ellipses from "../../assets/ellipses.png";
import type { KeyLearningsData } from "../../utils/mockProjects";
import { KeylearningsMobile } from "./KeylearningsMobile";

interface LearningProps {
  keyLearnings: KeyLearningsData;
}

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return isMobile;
};

export const Keylearnings: React.FC<LearningProps> = ({ keyLearnings }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const learningRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isMobile = useIsMobile();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = learningRefs.current.findIndex(
              (ref) => ref === entry.target,
            );
            if (index !== -1) {
              setActiveIndex(index);
            }
          }
        });
      },
      {
        root: scrollContainerRef.current,
        threshold: 0.5,
      },
    );

    learningRefs.current.forEach((ref) => {
      if (ref) {
        observer.observe(ref);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleDotClick = (index: number) => {
    const ref = learningRefs.current[index];
    if (ref) {
      ref.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <>
      {isMobile ? (
        <KeylearningsMobile keyLearnings={keyLearnings} />
      ) : (
        <div className="mt-20 w-full flex flex-col justify-between items-center">
          <div className="w-full flex justify-between items-start">
            <p className="font-gilroy-regular text-2xl text-white">
              Key Learnings
            </p>
            {/* <img className="mt-2" src={ellipses} alt="ellipses" /> */}
            <p className="w-4xl text-white text-5xl font-gilroy-bold leading-[48px]">
              Learning that great ideas move fastest when everyone’s aligned —
              and stay strongest when tech meets craft."
            </p>
            {/* <img className="mt-2" src={ellipses} alt="ellipses" /> */}
          </div>
          <div className="h-screen sticky flex justify-between items-center">
            <div className="flex gap-20 justify-between  items-center w-full">
              <div className="flex w-full h-full justify-center">
                <img
                  src={keyLearnings.img}
                  alt="learning1"
                  className=" max-h-[70vh]"
                />
              </div>

              <div className="flex flex-col w-full">
                <p className="text-white pt-8 py-24 text-2xl font-gilroy-medium leading-10">
                  Furthermore
                </p>
                <div className="flex gap-24">
                  <div className="flex flex-col items-center">
                    {keyLearnings?.learnings?.map((_, index) => (
                      <div
                        key={index}
                        onClick={() => handleDotClick(index)}
                        className={`cursor-pointer transition-all duration-300 ${
                          activeIndex === index
                            ? "w-0.5 h-50 bg-white my-2"
                            : "w-2.5 h-2.5 rounded-full bg-white my-3"
                        }`}
                      />
                    ))}
                  </div>

                  <div
                    ref={scrollContainerRef}
                    className="relative flex flex-col h-[40vh] overflow-y-auto gap-52 last:pb-52"
                  >
                    {keyLearnings?.learnings?.map((learning, index) => (
                      <div
                        key={index}
                        ref={(el) => {
                          learningRefs.current[index] = el;
                        }}
                        className="items-end flex flex-col"
                      >
                        <div className="flex w-full">
                          <div className="text-5xl font-gilroy-bold leading-[48px] mr-4">
                            {index + 1}.
                          </div>
                          <h1 className="text-5xl font-gilroy-bold leading-[48px]">
                            {learning.title}
                          </h1>
                        </div>
                        <p className="text-2xl w-11/12 font-gilroy-light leading-relaxed mt-4">
                          {learning.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
